import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import t from '../helpers/calculator_translations';
import type { CalculatorRequest, CalculatorResponse } from '../interfaces/Calculator.model';
import type { Window } from '../interfaces/Window.model';
import { reportError } from '../lib/sentry';
import { type CalculatorConfig, CalculatorConfigGroup } from './CalculatorConfigGroup';
import { CalculatorForm } from './CalculatorForm';
import { CalculatorLoading } from './CalculatorLoading';
import { CalculatorResultSet } from './CalculatorResultSet';

interface CalculatorProps {
    locale: string;
    phoneNumber: string | null;
    phoneNumberIntl: string | null;
    email: string | null;
    calculatorConfig: CalculatorConfig;
    aiMinimumWordPrice: string | null;
    economyMinimumWordPrice: string | null;
    excellentMinimumWordPrice: string | null;
    expressMinimumWordPrice: string | null;
}

/**
 * The request never completed: a dropped connection, or the visitor navigating
 * away mid-request. Ordinary on mobile and nothing we can act on, so it is the
 * one failure we deliberately do not report.
 */
class RequestNotCompleted extends Error {
    constructor() {
        super('Calculator request did not complete');
        this.name = 'RequestNotCompleted';
    }
}

/**
 * Craft (or something in front of it) answered with a non-OK status. Carries the
 * request payload and a short response snippet so Sentry can show what was sent
 * without needing nginx or Connect logs.
 */
class CalculatorHttpError extends Error {
    readonly status: number;
    readonly requestBody: Record<string, unknown>;
    readonly responseSnippet: string;

    constructor(status: number, requestBody: Record<string, unknown>, responseSnippet: string) {
        super(`Calculator request failed with status ${status}`);
        this.name = 'CalculatorHttpError';
        this.status = status;
        this.requestBody = requestBody;
        this.responseSnippet = responseSnippet;
    }
}

const Calculator = ({
    locale,
    phoneNumber,
    phoneNumberIntl,
    email,
    calculatorConfig,
    aiMinimumWordPrice,
    economyMinimumWordPrice,
    excellentMinimumWordPrice,
    expressMinimumWordPrice,
}: CalculatorProps) => {
    // Same defaults as TableCalculator, which has always started on a real
    // language pair. The translation keys hold a sensible pair per site.
    //
    // These are not cosmetic. This component asks for a price as soon as it
    // mounts, and Connect cannot answer a request with no languages: it
    // redirects, which surfaces as a failed request. Between switching these
    // fields to placeholders and this change, every first-time visitor sent
    // exactly that request. It was invisible for as long as the failure was
    // swallowed into an empty response.
    const defaultValues = {
        // 1000, the value this field started on before it was turned into a
        // placeholder. Deliberately not the lowest of calculatorConfig.wordsOptions:
        // those buckets belong to the table calculator, which picks from a list.
        // This form has a free number input, so a bucket boundary means nothing
        // here and 5000 would put the opening price higher than most visitors
        // are actually asking about.
        words: 1000,
        sourceLanguage: t('calculator.source_language.default'),
        targetLanguage: t('calculator.target_language.default'),
        discipline: 1,
    } as CalculatorRequest;

    // State for loading
    const [formHasChanges, setFormHasChanges] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean | null>(null);
    const [requestFailed, setRequestFailed] = useState<boolean>(false);
    const [calculated, setCalculated] = useState<boolean>(false);

    // Form starts empty on purpose, so the fields show their placeholders and the
    // visitor is invited to enter their own job. That is separate from
    // defaultValues above, which only feeds the request fired on mount so a price
    // is already on screen. Do not initialise these from defaultValues: that fills
    // the form in and takes the invitation away.
    const [words, setWords] = useState<number | null>(null);
    const [sourceLanguage, setSourceLanguage] = useState<string>('');
    const [targetLanguage, setTargetLanguage] = useState<string>('');
    const [discipline, setDiscipline] = useState<number | null>(null);

    // State for request / response
    const [requestedData, setRequestedData] = useState<CalculatorRequest | null>(null);
    const [response, setResponse] = useState<CalculatorResponse>({
        ai: {},
        economy: {},
        excellent: {},
        express: {},
        context: {},
        postEditingAvailable: false,
        advantages: [],
        maxAdvantages: 0,
    });

    const [services, setServices] = useState<Set<string>>(
        new Set(['economy', 'excellent', 'express'])
    );

    const minimumWordPrices = {
        ai: aiMinimumWordPrice ?? null,
        economy: economyMinimumWordPrice ?? null,
        excellent: excellentMinimumWordPrice ?? null,
        express: expressMinimumWordPrice ?? null,
    };

    // Scroll to results
    const scrollTo = useRef<null | HTMLDivElement>(null);
    const executeScroll = () => {
        if (scrollTo.current === null) return;

        scrollTo.current.scrollIntoView({ behavior: 'smooth' });
    };

    // See if form has changes
    useEffect(() => {
        if (
            (requestedData?.words ?? defaultValues.words) !== words ||
            (requestedData?.sourceLanguage ?? defaultValues.sourceLanguage) !== sourceLanguage ||
            (requestedData?.targetLanguage ?? defaultValues.targetLanguage) !== targetLanguage ||
            (requestedData?.discipline ?? defaultValues.discipline) !== discipline
        ) {
            setFormHasChanges(true);
        }
    }, [
        words,
        sourceLanguage,
        targetLanguage,
        discipline,
        defaultValues.discipline,
        defaultValues.words,
        requestedData?.discipline,
        defaultValues.targetLanguage,
        requestedData?.words,
        requestedData?.targetLanguage,
        requestedData?.sourceLanguage,
        defaultValues.sourceLanguage,
    ]);

    const request = (requestData: CalculatorRequest) => {
        setLoading(true);
        setRequestFailed(false);
        setFormHasChanges(false);

        setRequestedData(requestData);

        const requestBody = {
            domein_id: calculatorConfig.domainId ?? 1,
            brontaal: requestData.sourceLanguage,
            doeltaal: requestData.targetLanguage,
            vakgebied_id: requestData.discipline,
            aantal_woorden: requestData.words,
        };

        const requestOptions = {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        };

        fetch('/calculator/request', requestOptions)
            // Placed directly on the fetch so it sees only the fetch rejecting,
            // never a failure from the handlers below. That distinction is what
            // lets the catch at the end decide whether something is worth
            // reporting.
            .catch(() => {
                throw new RequestNotCompleted();
            })
            .then(async (response) => {
                if (!response.ok) {
                    let responseSnippet = '';
                    try {
                        responseSnippet = (await response.text()).slice(0, 500);
                    } catch {
                        // Ignore; the status alone is still worth reporting.
                    }

                    throw new CalculatorHttpError(response.status, requestBody, responseSnippet);
                }

                return response.json();
            })
            .then((data) => {
                data.maxAdvantages = Math.max(
                    [...data.advantages].filter((advantage) => advantage.economy).length,
                    [...data.advantages].filter((advantage) => advantage.excellent).length,
                    [...data.advantages].filter((advantage) => advantage.express).length
                );

                setResponse(data);

                if (
                    data.ai.totalPrice !== null ||
                    (data.economy.totalPrice === null && data.excellent.totalPrice === null)
                ) {
                    setServices(new Set(['ai', 'excellent', 'express']));
                } else {
                    setServices(new Set(['economy', 'excellent', 'express']));
                }
            })
            .catch((error: unknown) => {
                // Without this the rejection stays unhandled and, worse, the loading
                // flag set above is never cleared: the visitor is left staring at a
                // spinner with no prices, no message and no way to try again.
                setRequestFailed(true);

                // Everything except a request that never completed is reported. That
                // covers an error status as well as anything the handlers above throw
                // on a response that did arrive, such as a payload missing the fields
                // they read. Those used to reach Sentry as unhandled rejections, so
                // catching them here without reporting would quietly lose them.
                if (!(error instanceof RequestNotCompleted)) {
                    const extra =
                        error instanceof CalculatorHttpError
                            ? {
                                  endpoint: '/calculator/request',
                                  method: 'PUT',
                                  status: error.status,
                                  requestBody: error.requestBody,
                                  responseSnippet: error.responseSnippet,
                              }
                            : {
                                  endpoint: '/calculator/request',
                                  method: 'PUT',
                                  requestBody,
                              };

                    reportError(error, { extra });
                }
            })
            .finally(() => setLoading(false));
    };

    const retry = () => {
        if (requestedData === null) {
            return;
        }

        request(requestedData);

        // The button lives inside the error message, which request() clears in the
        // same render, so it is gone by the time the retry runs. Without moving
        // focus it would fall back to the document body and a keyboard or screen
        // reader user would lose their place entirely.
        scrollTo.current?.focus();
    };

    const submit = () => {
        setCalculated(true);

        const requestData = {
            words: words,
            sourceLanguage: sourceLanguage,
            targetLanguage: targetLanguage,
            discipline: discipline,
        } as CalculatorRequest;

        localStorage.setItem('calculatedData', JSON.stringify(requestData));

        request(requestData);
        executeScroll();
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: init effect runs once on mount
    useEffect(() => {
        // Copy, so the assignments below cannot mutate the object this render
        // also compares the form against.
        const requestValues = { ...defaultValues };

        // Use local storage if available
        if (localStorage.getItem('calculatedData') !== null) {
            const defaultsFromHistory = JSON.parse(localStorage.getItem('calculatedData') ?? '{}');

            setCalculated(true);

            // This runs outside the form, so the input's own minimum does not
            // apply here. A stored count of zero predates that minimum and is
            // the one number Connect cannot price, so it has to fall back rather
            // than be replayed on every visit. Same for anything that is not a
            // usable number at all.
            requestValues.words =
                typeof defaultsFromHistory.words === 'number' &&
                Number.isFinite(defaultsFromHistory.words) &&
                defaultsFromHistory.words > 0
                    ? defaultsFromHistory.words
                    : defaultValues.words;
            // `||` rather than `??` for the two languages: a stored empty string
            // has to fall back to the default as well, because a request without
            // languages is the other thing Connect answers with a redirect.
            requestValues.sourceLanguage =
                defaultsFromHistory.sourceLanguage || defaultValues.sourceLanguage;
            requestValues.targetLanguage =
                defaultsFromHistory.targetLanguage || defaultValues.targetLanguage;
            requestValues.discipline = defaultsFromHistory.discipline ?? defaultValues.discipline;

            // Update form values
            setWords(requestValues.words);
            setSourceLanguage(requestValues.sourceLanguage);
            setTargetLanguage(requestValues.targetLanguage);
            setDiscipline(requestValues.discipline);
        }

        request(requestValues);
    }, []);

    return (
        <div className={'calculator__form container p-0'}>
            <div className={'row'}>
                <CalculatorForm
                    locale={locale}
                    calculatorConfig={calculatorConfig}
                    words={words}
                    setWords={setWords}
                    sourceLanguage={sourceLanguage}
                    setSourceLanguage={setSourceLanguage}
                    targetLanguage={targetLanguage}
                    setTargetLanguage={setTargetLanguage}
                    discipline={discipline}
                    setDiscipline={setDiscipline}
                    formHasChanges={formHasChanges}
                    submit={submit}
                />
            </div>

            <div className={'row'}>
                <div className={'col-12 mt-3 position-relative'}>
                    {loading && <CalculatorLoading color={'secondary'} />}

                    {/*
                        Kept mounted at all times rather than rendered alongside its
                        text: a live region that appears together with its content is
                        not announced by several screen reader and browser pairs, which
                        register the region only after that render pass.
                    */}
                    <div
                        role="alert"
                        className={requestFailed ? 'alert alert-warning' : 'visually-hidden'}
                    >
                        {requestFailed && (
                            <>
                                {t('calculator.request_failed')}{' '}
                                <button
                                    type="button"
                                    className={'btn btn-link p-0 align-baseline'}
                                    onClick={retry}
                                >
                                    {t('calculator.request_failed_retry')}
                                </button>
                            </>
                        )}
                    </div>

                    <div className={'position-relative'}>
                        <div
                            id="calculator__results"
                            className={'calculator__results mt-5'}
                            ref={scrollTo}
                            // Not in the tab order, but focusable in code so retry can
                            // hand focus to the results it is about to refresh.
                            tabIndex={-1}
                        >
                            {Array.from(services).map((type: string) => {
                                const data = response[type as keyof CalculatorResponse];
                                const minimumWordPrice =
                                    minimumWordPrices[type as keyof typeof minimumWordPrices];

                                return (
                                    <CalculatorResultSet
                                        key={type}
                                        calculatorConfig={calculatorConfig}
                                        locale={locale}
                                        data={data}
                                        wordRepetitionDiscountInfoText={
                                            response.context?.wordRepetitionDiscountInfoText
                                        }
                                        welcomeDiscountInfoText={
                                            response.context?.welcomeDiscountInfoText
                                        }
                                        sourceLanguage={sourceLanguage}
                                        targetLanguage={targetLanguage}
                                        type={type}
                                        advantages={response.advantages}
                                        maxAdvantages={response.maxAdvantages}
                                        minimumWordPrice={minimumWordPrice}
                                        calculated={calculated}
                                    />
                                );
                            })}
                        </div>

                        {!calculatorConfig.showPriceInclVat && (
                            <div
                                className={
                                    'text-center w-100 mb-3 calculator__result__price_details calculator__result__price_excl_vat'
                                }
                            >
                                {t('calculator.exclude_vat')}
                                <br />
                                {t('calculator.indicative_calculation')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {services.has('ai') === false && response.postEditingAvailable && (
                <div className={loading ? 'd-none' : 'row mt-5'}>
                    <div
                        className={'col-12 text-center'}
                        dangerouslySetInnerHTML={{
                            __html: response.context.calculatorPostEditingText ?? '',
                        }}
                    />
                </div>
            )}

            <div className={loading ? 'd-none' : 'row mt-5'}>
                <div
                    className={
                        'col-12 col-lg-8 col-xl-6 d-flex flex-wrap flex-md-nowrap justify-content-between align-items-center mb-5 mb-lg-0'
                    }
                >
                    {response.context.calculatorCtaImage && (
                        <img
                            className={'quote-image mx-auto me-md-5 mb-2'}
                            style={{ width: '150px', height: '150px', borderRadius: '50%' }}
                            src={response.context.calculatorCtaImage}
                            alt={response.context.calculatorCtaHeading}
                        />
                    )}
                    <div className={'text-center text-md-start'}>
                        <span className={'h4'}>{response.context.calculatorCtaHeading}</span>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: response.context.calculatorCtaText ?? '',
                            }}
                        />
                    </div>
                </div>
                <div
                    className={
                        loading
                            ? 'd-none'
                            : 'col-12 col-lg-4 col-xl-6 d-flex flex-wrap flex-sm-nowrap flex-lg-wrap justify-content-around text-center align-items-center'
                    }
                >
                    <div className={'py-3 w-100'}>
                        {t('calculator.call_us')}
                        <br />
                        <i className={'fa fa-phone me-2'} />
                        <a target="_top" href={`tel:${phoneNumberIntl}`}>
                            {phoneNumber}
                        </a>
                    </div>
                    <div className={'py-3 w-100'}>
                        {t('calculator.contact_us')}
                        <br />
                        <i className={'fa fa-envelope me-2'} />
                        <a href={`mailto:${email}`}>{email}</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const calculatorElements = document.querySelectorAll(
    '.calculator-component'
) as NodeListOf<HTMLElement>;
calculatorElements.forEach((element) => {
    const locale: string = (window as Window).locale ?? 'nl';
    const calculatorConfig = CalculatorConfigGroup(locale);
    const root = createRoot(element);

    root.render(
        <Calculator
            locale={locale}
            calculatorConfig={calculatorConfig}
            phoneNumber={element.getAttribute('data-phone-number')}
            phoneNumberIntl={element.getAttribute('data-phone-number-intl')}
            email={element.getAttribute('data-email')}
            aiMinimumWordPrice={element.getAttribute('data-ai-minimum-word-price')}
            economyMinimumWordPrice={element.getAttribute('data-economy-minimum-word-price')}
            excellentMinimumWordPrice={element.getAttribute('data-excellent-minimum-word-price')}
            expressMinimumWordPrice={element.getAttribute('data-express-minimum-word-price')}
        />
    );
});
